import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  Loader2,
  MapPin,
  MessageCircle,
  Phone,
  Receipt,
  TrendingUp,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';
import toast from 'react-hot-toast';
import FinanceLockGate from '../components/FinanceLockGate';

const MANDATORY_CONSULTATION_NAME = 'Oral Examination / Consultation';
const RUPEE_SYMBOL = '\u20B9';

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value) {
  return `${RUPEE_SYMBOL}${toNumber(value, 0).toFixed(2)}`;
}

function getNextMonthSameDateValue() {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().split('T')[0];
}

function normalizeWhatsAppPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `91${digits}`;
  if (digits.startsWith('91') && digits.length >= 12) return digits;
  return digits;
}

function buildWhatsAppLaunchUrl(phone, message) {
  const normalizedPhone = normalizeWhatsAppPhone(phone);
  const cleanMessage = String(message || '').trim();

  if (!normalizedPhone || !cleanMessage) {
    return '';
  }

  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(cleanMessage)}`;
}

function buildInvoiceRowFromTreatment(treatment) {
  const site = String(treatment.toothNumber || '').trim();
  const manuallyAdded = Boolean(treatment.manuallyAdded || treatment.status === 'manual');
  return {
    rowKey: `${manuallyAdded ? 'manual::' : ''}${String(treatment.id)}::${site.toLowerCase() || 'no-site'}`,
    treatmentId: treatment.id,
    sourceType: treatment.sourceType || (String(treatment.id).startsWith('custom-') ? 'custom' : 'master'),
    customTreatmentId: treatment.customTreatmentId || null,
    name: treatment.name,
    domain: treatment.domain || '',
    quantity: Math.max(0, toNumber(treatment.quantity, 1)),
    rate: toNumber(treatment.sellingPrice, 0),
    fixedCostPerUnit: toNumber(treatment.fixedCost, 0),
    variableCostPerUnit: toNumber(treatment.variableCost, 0),
    labCostPerUnit: toNumber(treatment.labCost, 0),
    consultantFeesPerUnit: toNumber(treatment.consultantFees, 0),
    breakEvenCostPerUnit: toNumber(treatment.breakEvenCost, 0),
    durationPerUnit: toNumber(treatment.duration, 0),
    mandatory: Boolean(treatment.mandatory),
    manuallyAdded,
    sourceStatuses: treatment.status ? [treatment.status] : [],
    toothNumbers: site ? [site] : [],
    prescriptionTreatmentIds: treatment.prescriptionTreatmentId
      ? [treatment.prescriptionTreatmentId]
      : [],
  };
}

function buildInvoiceRowsFromPrefill(prefillTreatments = []) {
  const grouped = new Map();

  for (const treatment of prefillTreatments) {
    if (!treatment?.id || !treatment?.name) {
      continue;
    }

    const key = `${String(treatment.id)}::${String(treatment.toothNumber || '').trim().toLowerCase() || 'no-site'}`;
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, buildInvoiceRowFromTreatment(treatment));
      continue;
    }

    existing.quantity += Math.max(0, toNumber(treatment.quantity, 1));
    existing.mandatory = existing.mandatory || Boolean(treatment.mandatory);

    if (treatment.status && !existing.sourceStatuses.includes(treatment.status)) {
      existing.sourceStatuses.push(treatment.status);
    }

    if (treatment.toothNumber && !existing.toothNumbers.includes(treatment.toothNumber)) {
      existing.toothNumbers.push(treatment.toothNumber);
    }

    if (
      treatment.prescriptionTreatmentId &&
      !existing.prescriptionTreatmentIds.includes(treatment.prescriptionTreatmentId)
    ) {
      existing.prescriptionTreatmentIds.push(treatment.prescriptionTreatmentId);
    }
  }

  return Array.from(grouped.values());
}

function buildMandatoryConsultationRow(pricingRows = []) {
  const consultation =
    pricingRows.find((item) => item.name === MANDATORY_CONSULTATION_NAME) || null;

  if (!consultation) {
    return null;
  }

  return {
    rowKey: String(consultation.id),
    treatmentId: consultation.id,
    name: consultation.name,
    domain: consultation.domain || '',
    quantity: 1,
    rate: toNumber(consultation.sellingPrice, 0),
    fixedCostPerUnit: toNumber(consultation.fixedCost, 0),
    variableCostPerUnit: toNumber(consultation.variableCost, 0),
    labCostPerUnit: toNumber(consultation.labCost, 0),
    consultantFeesPerUnit: toNumber(consultation.consultantFees, 0),
    breakEvenCostPerUnit: toNumber(consultation.breakEvenCost, 0),
    durationPerUnit: toNumber(consultation.duration, 0),
    mandatory: true,
    sourceStatuses: ['mandatory'],
    toothNumbers: [],
    prescriptionTreatmentIds: [],
  };
}

function applyPricingMetricsToRows(rows = [], pricingRows = []) {
  const pricingByTreatmentId = new Map(
    pricingRows.map((row) => [String(row.id), row])
  );

  return rows.map((row) => {
    const pricedRow = pricingByTreatmentId.get(String(row.treatmentId));
    if (!pricedRow) {
      return row;
    }

    return {
      ...row,
      rate: toNumber(pricedRow.sellingPrice, row.rate),
      fixedCostPerUnit: toNumber(pricedRow.fixedCost, row.fixedCostPerUnit),
      variableCostPerUnit: toNumber(pricedRow.variableCost, row.variableCostPerUnit),
      labCostPerUnit: toNumber(pricedRow.labCost, row.labCostPerUnit),
      consultantFeesPerUnit: toNumber(pricedRow.consultantFees, row.consultantFeesPerUnit),
      breakEvenCostPerUnit: toNumber(pricedRow.breakEvenCost, row.breakEvenCostPerUnit),
      durationPerUnit: toNumber(pricedRow.duration, row.durationPerUnit),
    };
  });
}

function ensureMandatoryConsultationRow(rows = [], pricingRows = []) {
  const alreadyIncluded = rows.some((row) => row.name === MANDATORY_CONSULTATION_NAME);
  if (alreadyIncluded) {
    return rows;
  }

  const mandatoryRow = buildMandatoryConsultationRow(pricingRows);
  if (!mandatoryRow) {
    return rows;
  }

  return [mandatoryRow, ...rows];
}

function getEffectiveQuantity(row) {
  return Math.max(0, Math.trunc(toNumber(row?.quantity, 0)));
}

function getSourceBadgeLabel(sourceStatuses = [], mandatory = false) {
  if (mandatory || sourceStatuses.includes('mandatory')) {
    return 'Mandatory';
  }

  if (sourceStatuses.includes('ongoing') && sourceStatuses.includes('completed')) {
    return 'Ongoing + Completed';
  }

  if (sourceStatuses.includes('ongoing')) {
    return 'Ongoing';
  }

  if (sourceStatuses.includes('completed')) {
    return 'Completed';
  }

  if (sourceStatuses.includes('manual')) {
    return 'Manual';
  }

  return 'Billed';
}

export default function MakeInvoice() {
  const { user } = useAuthStore();
  const can = (capability) => !Array.isArray(user?.capabilities) || user.capabilities.includes(capability);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prescriptionId = searchParams.get('prescriptionId');
  const appointmentId = searchParams.get('appointmentId');

  const [loading, setLoading] = useState(true);
  const [prefillError, setPrefillError] = useState('');
  const [patient, setPatient] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [selectedManualTreatmentId, setSelectedManualTreatmentId] = useState('');
  const [supplementalBillingRequired, setSupplementalBillingRequired] = useState(false);
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [paymentMode, setPaymentMode] = useState('one-time');
  const [emiStep, setEmiStep] = useState(1);
  const [downPayment, setDownPayment] = useState('');
  const [emiPlan, setEmiPlan] = useState('3');
  const [customEmiMonths, setCustomEmiMonths] = useState('');
  const [emiStartDate, setEmiStartDate] = useState(getNextMonthSameDateValue());
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentCardVisible, setPaymentCardVisible] = useState(false);
  const [whatsappNumberEditorOpen, setWhatsappNumberEditorOpen] = useState(false);
  const [whatsappNumberDraft, setWhatsappNumberDraft] = useState('');
  const [sendBillPhoneOverride, setSendBillPhoneOverride] = useState('');
  const [pendingRemovalRow, setPendingRemovalRow] = useState(null);

  const invoiceLocked = Boolean(generatedInvoice?.id);

  useEffect(() => {
    let active = true;

    const bootstrapInvoicePage = async () => {
      setLoading(true);
      setPrefillError('');

      try {
        const [, pricingRes] = await Promise.all([
          api.get('/setup').catch(() => ({ data: null })),
          api.get('/pricing').catch(() => ({ data: [] })),
        ]);

        if (!active) {
          return;
        }

        const pricingPayload = Array.isArray(pricingRes.data) ? pricingRes.data : [];
        setPricingRows(pricingPayload);

        if (!prescriptionId) {
          setLineItems(applyPricingMetricsToRows(ensureMandatoryConsultationRow([], pricingPayload), pricingPayload));
          setPrefillError('Open this page from Prescription Pad to preload patient and treatment workflow data.');
          return;
        }

        const prefillRes = await api.get(`/prescriptions/${prescriptionId}/invoice-prefill`, {
          params: appointmentId ? { appointmentId } : {},
        });
        if (!active) {
          return;
        }

        const prefillRows = buildInvoiceRowsFromPrefill(prefillRes.data?.treatments || []);
        setPatient(prefillRes.data?.patient || null);
        setSupplementalBillingRequired(Boolean(prefillRes.data?.supplementalBillingRequired));
        setLineItems(
          applyPricingMetricsToRows(
            prefillRes.data?.mandatoryConsultationRequired === false
              ? prefillRows
              : ensureMandatoryConsultationRow(prefillRows, pricingPayload),
            pricingPayload
          )
        );
      } catch (err) {
        console.error(err);
        if (!active) {
          return;
        }

        setPrefillError(err.response?.data?.error || 'Failed to load invoice prefill');
        toast.error('Failed to load invoice prefill');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    bootstrapInvoicePage();

    return () => {
      active = false;
    };
  }, [appointmentId, prescriptionId]);

  useEffect(() => {
    if (!generatedInvoice?.id) {
      setPaymentCardVisible(false);
      return undefined;
    }

    setPaymentCardVisible(false);
    const timer = window.setTimeout(() => setPaymentCardVisible(true), 30);
    return () => window.clearTimeout(timer);
  }, [generatedInvoice?.id, paymentSummary?.type]);

  useEffect(() => {
    if (!sendBillPhoneOverride) {
      setWhatsappNumberDraft(patient?.phone || '');
    }
  }, [patient?.phone, sendBillPhoneOverride]);

  useEffect(() => {
    if (!pricingRows.length) {
      return;
    }

    setLineItems((currentRows) => applyPricingMetricsToRows(currentRows, pricingRows));
  }, [pricingRows]);

  const subtotal = useMemo(
    () =>
      lineItems.reduce(
        (sum, row) => sum + getEffectiveQuantity(row) * toNumber(row.rate, 0),
        0
      ),
    [lineItems]
  );

  const discountAmount = useMemo(() => {
    const rawValue = Math.max(0, toNumber(discountValue, 0));

    if (discountType === 'percent') {
      return (subtotal * rawValue) / 100;
    }

    return rawValue;
  }, [discountType, discountValue, subtotal]);

  const netPayable = useMemo(
    () => Math.max(0, subtotal - discountAmount),
    [subtotal, discountAmount]
  );

  const breakEvenCostTotal = useMemo(
    () =>
      lineItems.reduce(
        (sum, row) =>
          sum + getEffectiveQuantity(row) * toNumber(row.breakEvenCostPerUnit, 0),
        0
      ),
    [lineItems]
  );

  const safeDiscountAmount = useMemo(
    () => Math.max(0, subtotal - breakEvenCostTotal),
    [subtotal, breakEvenCostTotal]
  );

  const profitOrLoss = useMemo(
    () => netPayable - breakEvenCostTotal,
    [netPayable, breakEvenCostTotal]
  );

  const emiMonths = useMemo(() => {
    if (emiPlan === 'custom') {
      return Math.max(0, Math.trunc(toNumber(customEmiMonths, 0)));
    }

    return Math.max(0, Math.trunc(toNumber(emiPlan, 0)));
  }, [customEmiMonths, emiPlan]);

  const downPaymentAmount = Math.max(0, toNumber(downPayment, 0));
  const remainingAfterDownPayment = Math.max(0, netPayable - downPaymentAmount);
  const monthlyEmiAmount = emiMonths > 0 ? remainingAfterDownPayment / emiMonths : 0;

  const patientDetails = useMemo(
    () => [
      { label: 'Patient', value: patient?.name || '--', icon: UserRound },
      { label: 'Phone / WhatsApp', value: patient?.phone || '--', icon: Phone },
      { label: 'Patient ID', value: patient?.patientId || '--' },
      {
        label: 'Age / Gender',
        value:
          patient?.age || patient?.gender
            ? `${patient?.age || '--'} / ${patient?.gender || '--'}`
            : '--',
      },
      { label: 'Address / Area', value: patient?.address || '--', icon: MapPin },
    ],
    [patient]
  );

  const prescriptionReturnUrl = prescriptionId && patient?.id
    ? `/prescriptions/new?patientId=${patient.id}${appointmentId ? `&appointmentId=${appointmentId}` : ''}&id=${prescriptionId}`
    : '/dashboard';

  const updateRowQuantity = (rowKey, value) => {
    if (invoiceLocked) {
      return;
    }

    setLineItems((prev) =>
      prev.map((row) => {
        if (row.rowKey !== rowKey) {
          return row;
        }

        const nextQuantity = Math.max(0, Math.trunc(toNumber(value, 0)));
        return { ...row, quantity: nextQuantity };
      })
    );
  };

  const addManualTreatment = () => {
    if (invoiceLocked || supplementalBillingRequired || !selectedManualTreatmentId) return;
    const treatment = pricingRows.find((row) => String(row.id) === String(selectedManualTreatmentId));
    if (!treatment) return;
    const manualRow = buildInvoiceRowFromTreatment({ ...treatment, quantity: 1, status: 'manual', manuallyAdded: true });
    setLineItems((currentRows) => {
      const existing = currentRows.find((row) => row.rowKey === manualRow.rowKey);
      if (existing) {
        return currentRows.map((row) => row.rowKey === manualRow.rowKey
          ? { ...row, quantity: getEffectiveQuantity(row) + 1 }
          : row);
      }
      return [...currentRows, manualRow];
    });
    setSelectedManualTreatmentId('');
  };

  const requestManualTreatmentRemoval = (row) => {
    if (invoiceLocked || row?.mandatory || !row?.manuallyAdded) return;
    setPendingRemovalRow(row);
  };

  const confirmManualTreatmentRemoval = () => {
    if (invoiceLocked || !pendingRemovalRow?.manuallyAdded || pendingRemovalRow?.mandatory) {
      setPendingRemovalRow(null);
      return;
    }
    setLineItems((currentRows) => currentRows.filter((row) => row.rowKey !== pendingRemovalRow.rowKey));
    setPendingRemovalRow(null);
  };

  const activeBillableLineItems = useMemo(
    () => lineItems.filter((row) => getEffectiveQuantity(row) > 0),
    [lineItems]
  );

  const buildInvoicePayload = (mode) => ({
    patientId: patient?.id,
    appointmentId: appointmentId ? Number(appointmentId) : null,
    prescriptionId: prescriptionId ? Number(prescriptionId) : null,
    treatments: activeBillableLineItems.map((row) => ({
      id: row.treatmentId,
      sourceType: row.sourceType,
      customTreatmentId: row.customTreatmentId,
      quantity: getEffectiveQuantity(row),
      price: toNumber(row.rate, 0),
      toothNumber: row.toothNumbers.length === 1 ? row.toothNumbers[0] : null,
      prescriptionTreatmentIds: row.prescriptionTreatmentIds,
    })),
    paymentType: mode === 'emi' ? 'EMI' : 'OneTime',
    downPayment: mode === 'emi' ? downPaymentAmount : 0,
    emiMonths: mode === 'emi' ? emiMonths : null,
    emiStartDate: mode === 'emi' ? emiStartDate : null,
    discountAmount,
  });

  const ensureInvoiceCreated = async (mode) => {
    if (generatedInvoice?.id) {
      return generatedInvoice;
    }

    const payload = buildInvoicePayload(mode);

    const res = await api.post('/patients/invoice', payload);
    setGeneratedInvoice(res.data);
    return res.data;
  };

  const validateCompletedWorkflow = async (invoiceId) => {
    const validationRequests = [
      api.get(`/invoices/${invoiceId}`),
      can('inventory.audit') ? api.get(`/inventory/audit/${invoiceId}`) : Promise.resolve({ data: { rows: [] } }),
      api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
    ];

    if (prescriptionId && can('clinical.view')) {
      validationRequests.push(api.get(`/prescriptions/${prescriptionId}`));
      validationRequests.push(
        api.get(`/prescriptions/${prescriptionId}/pdf`, { responseType: 'blob' })
      );
    }

    const [invoiceRes, auditRes] = await Promise.all(validationRequests);
    const auditRows = Array.isArray(auditRes?.data?.rows) ? auditRes.data.rows : [];

    if (can('inventory.audit') && activeBillableLineItems.length > 0 && auditRows.length === 0) {
      throw new Error('Inventory Audit draft was not created for this invoice.');
    }

    return {
      invoice: invoiceRes?.data || null,
      auditRows,
    };
  };

  const buildInventoryAuditWorkflowUrl = (invoice, mode) => {
    const params = new URLSearchParams({
      workflow: 'post-billing',
      paymentMode: mode,
    });

    if (prescriptionId) {
      params.set('prescriptionId', prescriptionId);
    }

    if (invoice?.invoiceNumber) {
      params.set('invoiceNumber', invoice.invoiceNumber);
    }

    return `/inventory/audit/${invoice.id}?${params.toString()}`;
  };

  const routeInvoiceIntoAuditWorkflow = async (mode) => {
    const invoice = await ensureInvoiceCreated(mode);
    await validateCompletedWorkflow(invoice.id);
    const whatsappStatus = invoice?.whatsappStatus || null;

    const nextSummary = {
      type: mode,
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      auditReady: true,
      whatsappStatus,
    };

    setGeneratedInvoice(invoice);
    setPaymentSummary(nextSummary);
    toast.success('Payment message ready');
    return invoice;
  };

  const handleSendBill = () => {
    if (paymentMode === 'emi' && (!generatedInvoice?.id || String(generatedInvoice?.paymentType || '').toUpperCase() !== 'EMI')) {
      toast.error('Finalize EMI Billing first');
      return;
    }
    const messageText = paymentSummary?.whatsappStatus?.messageText || generatedInvoice?.whatsappStatus?.messageText || '';

    if (!String(messageText || '').trim()) {
      toast.error('Payment message is not ready yet. Try again after saving the invoice.');
      return;
    }

    const normalizedPhone = normalizeWhatsAppPhone(sendBillPhoneOverride || patient?.phone);
    if (!normalizedPhone || normalizedPhone.length < 10) {
      toast.error('Patient WhatsApp number is missing - update it and try again');
      return;
    }

    const whatsappUrl = buildWhatsAppLaunchUrl(normalizedPhone, messageText);
    if (!whatsappUrl) {
      toast.error('WhatsApp could not be opened. You can still mark payment received.');
      return;
    }

    const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      toast.error('WhatsApp could not be opened. You can still mark payment received.');
    }
  };

  const handleUseWhatsappNumber = () => {
    const normalizedPhone = normalizeWhatsAppPhone(whatsappNumberDraft);

    if (!normalizedPhone || normalizedPhone.length < 10) {
      toast.error('Enter a valid WhatsApp number');
      return;
    }

    setSendBillPhoneOverride(normalizedPhone);
    setWhatsappNumberDraft(normalizedPhone);
    setWhatsappNumberEditorOpen(false);
    toast.success('WhatsApp number ready for this send');
  };

  const handlePaymentReceived = async () => {
    if (!generatedInvoice?.id) {
      toast.error('Invoice is not ready yet');
      return;
    }

    const dueNow = Number(paymentSummary?.whatsappStatus?.amountDueNow ?? generatedInvoice?.whatsappStatus?.amountDueNow ?? generatedInvoice?.totalAmount ?? 0);
    if (dueNow <= 0) { toast.success('This invoice is already fully paid.'); return; }
    const entered = window.prompt('Amount received', dueNow.toFixed(2));
    if (entered === null) return;
    const amount = Number(entered);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Enter a valid payment amount'); return; }
    try {
      const { data } = await api.post('/payments/receive', { invoiceId: generatedInvoice.id, amount });
      toast.success(`Payment received: ${formatCurrency(data.amountReceived)}`);
      if (Number(data.outstanding) <= 0 && can('inventory.audit')) navigate(buildInventoryAuditWorkflowUrl(generatedInvoice, paymentSummary?.type || paymentMode || 'one-time'));
      else navigate('/dashboard');
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to record payment'); }
  };

  const paymentCardMode = paymentSummary?.type || null;
  const showOneTimePaymentCard = Boolean(generatedInvoice?.id && paymentCardMode === 'one-time');
  const showEmiPaymentCard = Boolean(generatedInvoice?.id && paymentCardMode === 'emi');
  const effectiveWhatsappNumber = normalizeWhatsAppPhone(sendBillPhoneOverride || patient?.phone);

  const renderPaymentReadyCard = () => (
    <div
      className={`overflow-hidden transition-all duration-500 ease-out ${
        paymentCardVisible ? 'mt-4 max-h-[620px] opacity-100' : 'mt-0 max-h-0 opacity-0'
      }`}
    >
      <section
        className={`rounded-[26px] border border-emerald-200 bg-emerald-50/90 p-5 shadow-[0_20px_44px_-34px_rgba(16,185,129,0.45)] transition-all duration-500 ease-out sm:p-6 ${
          paymentCardVisible ? 'translate-y-0' : 'translate-y-4'
        }`}
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h2 className="text-[18px] font-semibold text-emerald-950">
            Payment message ready
          </h2>
        </div>

        <div className="mt-4 rounded-[20px] border border-emerald-200 bg-white/95 p-3.5 shadow-inner">
          <button
            type="button"
            onClick={() => setWhatsappNumberEditorOpen((open) => !open)}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                Update WhatsApp Number
              </p>
              <p className="mt-1 text-[12px] font-medium text-slate-500">
                {effectiveWhatsappNumber
                  ? `Sending to +${effectiveWhatsappNumber}`
                  : 'Add a number for this send only'}
              </p>
            </div>
            <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
              {whatsappNumberEditorOpen ? 'Close' : 'Edit'}
            </span>
          </button>

          {whatsappNumberEditorOpen ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <input
                type="tel"
                value={whatsappNumberDraft}
                onChange={(event) => setWhatsappNumberDraft(event.target.value)}
                placeholder="Enter WhatsApp number"
                className="min-h-[46px] rounded-full border border-emerald-200 bg-emerald-50/50 px-4 text-[13px] font-semibold text-slate-700 outline-none transition-all focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
              />
              <button
                type="button"
                onClick={handleUseWhatsappNumber}
                className="min-h-[46px] rounded-full bg-emerald-700 px-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_22px_-18px_rgba(15,23,42,0.5)] transition-colors hover:bg-emerald-800"
              >
                Use this number
              </button>
            </div>
          ) : null}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleSendBill}
            className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,_#25D366_0%,_#16a34a_100%)] px-5 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_18px_30px_-22px_rgba(22,163,74,0.75)] transition-all hover:-translate-y-0.5 hover:shadow-[0_22px_36px_-22px_rgba(22,163,74,0.9)]"
          >
            <MessageCircle className="h-4 w-4" />
            Send Bill
          </button>

          <button
            type="button"
            onClick={handlePaymentReceived}
            className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,_#0f766e_0%,_#0f172a_100%)] px-5 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_18px_30px_-22px_rgba(15,23,42,0.75)] transition-all hover:-translate-y-0.5 hover:shadow-[0_22px_36px_-22px_rgba(15,23,42,0.9)]"
          >
            <ClipboardList className="h-4 w-4" />
            Payment Received
          </button>
        </div>
      </section>
    </div>
  );

  const handleOneTimePayment = async () => {
    if (!patient?.id) {
      toast.error('Patient details are required before billing');
      return;
    }

    if (activeBillableLineItems.length === 0) {
      toast.error('Add at least one billable line item');
      return;
    }

    setSavingInvoice(true);
    setProcessingPayment(true);

    try {
      await routeInvoiceIntoAuditWorkflow('one-time');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || err.message || 'Failed to prepare one-time payment');
    } finally {
      setSavingInvoice(false);
      setProcessingPayment(false);
    }
  };

  const handleEmiNext = () => {
    if (emiStep === 1) {
      if (downPaymentAmount < 0 || downPaymentAmount > netPayable) {
        toast.error('Down payment must be between 0 and the net payable amount');
        return;
      }
    }

    if (emiStep === 2 && emiMonths <= 0) {
      toast.error('Select a valid EMI duration');
      return;
    }

    setEmiStep((prev) => Math.min(3, prev + 1));
  };

  const handleEmiBack = () => {
    setEmiStep((prev) => Math.max(1, prev - 1));
  };

  const handleEmiPayment = async () => {
    if (!patient?.id) {
      toast.error('Patient details are required before billing');
      return;
    }

    if (activeBillableLineItems.length === 0) {
      toast.error('Add at least one billable line item');
      return;
    }

    if (emiMonths <= 0) {
      toast.error('Select a valid EMI duration');
      return;
    }

    if (!emiStartDate) {
      toast.error('EMI start date is required');
      return;
    }

    if (downPaymentAmount < 0 || downPaymentAmount > netPayable) {
      toast.error('Down payment must be between 0 and the net payable amount');
      return;
    }

    setSavingInvoice(true);
    setProcessingPayment(true);

    try {
      await routeInvoiceIntoAuditWorkflow('emi');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || err.message || 'Failed to prepare EMI billing');
    } finally {
      setSavingInvoice(false);
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] px-6 py-8">
        <div className="mx-auto max-w-[1180px]">
          <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center gap-3 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              Loading invoice workflow...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(224,231,255,0.34),_transparent_28%),linear-gradient(180deg,_#fbfcff_0%,_#f1f5f9_100%)] px-4 py-5 sm:px-6 lg:px-8">
      {pendingRemovalRow ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="remove-invoice-treatment-title" className="w-full max-w-md rounded-[24px] border border-slate-200 bg-white p-6 shadow-2xl">
            <h2 id="remove-invoice-treatment-title" className="text-lg font-semibold text-slate-900">Remove this treatment from this invoice?</h2>
            <p className="mt-2 text-sm text-slate-500">{pendingRemovalRow.name} will be removed only from this unfinalized invoice.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setPendingRemovalRow(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={confirmManualTreatmentRemoval} className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700">Remove</button>
            </div>
          </section>
        </div>
      ) : null}
      <div className="mx-auto max-w-[1240px] space-y-6">
        <div className="rounded-[30px] border border-white/90 bg-white/95 shadow-[0_34px_90px_-68px_rgba(15,23,42,0.4)] backdrop-blur">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <button
                type="button"
                onClick={() => navigate(prescriptionReturnUrl)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] transition hover:border-blue-200 hover:text-blue-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Prescription Pad
              </button>
              <h1 className="mt-4 text-[28px] font-semibold tracking-[-0.02em] text-slate-900">
                Invoice
              </h1>
              <p className="mt-2 text-[14px] font-medium text-slate-500">
                Review the patient&apos;s treatments, confirm the final amount, and choose how
                the patient will pay.
              </p>
            </div>

            {generatedInvoice?.invoiceNumber ? (
              <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-[12px] font-semibold text-emerald-700">
                Invoice Ready: {generatedInvoice.invoiceNumber}
              </div>
            ) : null}
          </div>

          <div className="space-y-6 px-6 py-7 sm:px-8">
            {prefillError ? (
              <div className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-[13px] font-medium text-amber-800">
                {prefillError}
              </div>
            ) : null}

            {patient ? (
              <section className="rounded-[24px] border border-slate-200 bg-[#F7F9FC] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] sm:px-5">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                  {patientDetails.map((detail) => {
                    const Icon = detail.icon;

                    return (
                      <div key={detail.label} className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {detail.label}
                        </p>
                        <div className="mt-2.5 flex items-center gap-2">
                          {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
                          <p className="truncate text-[13px] font-semibold text-slate-900">
                            {detail.value}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_380px]">
              <section className="rounded-[26px] border border-slate-200 bg-white shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)]">
                <div className="border-b border-slate-100 px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <Receipt className="h-4 w-4 text-indigo-600" />
                    <h2 className="text-[16px] font-semibold text-slate-900">Invoice Line Items</h2>
                  </div>
                  <p className="mt-3 text-center text-sm font-medium leading-5 text-slate-600">
                    Set quantity to 0 for anything you are not billing in this visit
                  </p>
                </div>

                {supplementalBillingRequired ? (
                  <div className="mx-5 mt-4 rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold text-amber-800">
                    New performed treatment requires billing and inventory completion before finalizing.
                  </div>
                ) : (
                  <div className="grid gap-2 border-b border-slate-100 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <label className="sr-only" htmlFor="manual-invoice-treatment">Performed treatment</label>
                    <select
                      id="manual-invoice-treatment"
                      value={selectedManualTreatmentId}
                      disabled={invoiceLocked}
                      onChange={(event) => setSelectedManualTreatmentId(event.target.value)}
                      className="w-full rounded-[14px] border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] font-semibold text-slate-700 outline-none focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50 disabled:opacity-60"
                    >
                      <option value="">Add performed treatment from pricing catalog</option>
                      {pricingRows.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={addManualTreatment}
                      disabled={invoiceLocked || !selectedManualTreatmentId}
                      className="rounded-[14px] bg-indigo-600 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Add Treatment
                    </button>
                  </div>
                )}

                <div className="mobile-table-scroll">
                  <table className="mobile-card-table min-w-full divide-y divide-slate-100">
                    <thead>
                      <tr className="text-left">
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Treatment
                        </th>
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Quantity
                        </th>
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Rate (Selling Price)
                        </th>
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Amount
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {lineItems.map((row) => {
                        const rowAmount = getEffectiveQuantity(row) * toNumber(row.rate, 0);

                        return (
                          <tr key={row.rowKey} className="align-top">
                            <td data-label="Treatment" className="px-5 py-4">
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-[14px] font-semibold text-slate-900">
                                    {row.name}
                                  </p>
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                    {getSourceBadgeLabel(row.sourceStatuses, row.mandatory)}
                                  </span>
                                  {!invoiceLocked && row.manuallyAdded && !row.mandatory ? (
                                    <button
                                      type="button"
                                      onClick={() => requestManualTreatmentRemoval(row)}
                                      aria-label={`Remove ${row.name} from invoice`}
                                      title="Remove from this invoice"
                                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-600 transition hover:border-rose-300 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-200"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  ) : null}
                                </div>

                                {row.toothNumbers.length > 0 ? (
                                  <p className="text-[12px] font-medium text-slate-500">
                                    Tooth: {row.toothNumbers.join(', ')}
                                  </p>
                                ) : null}
                              </div>
                            </td>

                            <td data-label="Quantity" className="px-5 py-4">
                              <input
                                type="number"
                                min="0"
                                value={row.quantity}
                                disabled={invoiceLocked}
                                onChange={(event) =>
                                  updateRowQuantity(row.rowKey, event.target.value)
                                }
                                className="w-24 rounded-[14px] border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] font-semibold text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                              />
                            </td>

                            <td data-label="Rate" className="px-5 py-4">
                              <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] font-semibold text-slate-700">
                                {formatCurrency(row.rate)}
                              </div>
                            </td>

                            <td data-label="Amount" className="px-5 py-4">
                              <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] font-semibold text-slate-900">
                                {formatCurrency(rowAmount)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {lineItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            data-full-row="true"
                            className="px-5 py-10 text-center text-[13px] font-medium text-slate-500"
                          >
                            No billable treatments available yet.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="space-y-6">
                <section className="rounded-[26px] border border-slate-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#f8fbff_100%)] p-6 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)]">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-600">
                    <ClipboardList className="h-4 w-4" />
                    Financial Summary
                  </div>

                  <div className="mt-5 space-y-4">
                    <div className="flex items-center justify-between text-[14px] text-slate-500">
                      <span>Subtotal</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(subtotal)}</span>
                    </div>

                    <div className="rounded-[20px] border border-slate-200 bg-slate-50/80 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Discount
                        </p>
                        <div className="flex gap-2">
                          {['amount', 'percent'].map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => !invoiceLocked && setDiscountType(type)}
                              disabled={invoiceLocked}
                              className={`rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                                discountType === type
                                  ? 'bg-white text-slate-900 shadow-[0_6px_16px_rgba(15,23,42,0.08)]'
                                  : 'bg-slate-200/70 text-slate-500'
                              }`}
                            >
                              {type === 'amount' ? 'Flat' : 'Percent'}
                            </button>
                          ))}
                        </div>
                      </div>

                      <input
                        type="number"
                        value={discountValue}
                        disabled={invoiceLocked}
                        onChange={(event) => setDiscountValue(event.target.value)}
                        placeholder="0"
                        className="mt-3 w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-right text-[14px] font-semibold text-slate-900 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-70"
                      />

                      <p className="mt-3 text-[12px] font-medium text-slate-500">
                        Maximum safe discount before loss: {formatCurrency(safeDiscountAmount)}
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-[14px] text-slate-500">
                      <span>Break-even Cost Total</span>
                      <span className="font-semibold text-slate-900">
                        {formatCurrency(breakEvenCostTotal)}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Net Payable
                      </span>
                      <span className="mobile-safe-text text-[26px] font-semibold text-emerald-600 sm:text-[32px]">
                        {formatCurrency(netPayable)}
                      </span>
                    </div>
                  </div>
                </section>

                <section className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)]">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    <TrendingUp className="h-4 w-4 text-indigo-600" />
                    Case Profitability
                  </div>

                  <div className="mt-4 grid gap-3">
                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Break-even Cost
                      </p>
                      <p className="mt-2 text-[18px] font-semibold text-slate-900">
                        {formatCurrency(breakEvenCostTotal)}
                      </p>
                    </div>

                    <div
                      className={`rounded-[18px] border px-4 py-4 ${
                        profitOrLoss > 0
                          ? 'border-emerald-200 bg-emerald-50'
                          : profitOrLoss < 0
                          ? 'border-red-200 bg-red-50'
                          : 'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {profitOrLoss < 0 ? (
                          <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600" />
                        ) : (
                          <CheckCircle2
                            className={`mt-0.5 h-4 w-4 ${
                              profitOrLoss > 0 ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                          />
                        )}
                        <div>
                          <p
                            className={`text-[15px] font-semibold ${
                              profitOrLoss > 0
                                ? 'text-emerald-800'
                                : profitOrLoss < 0
                                ? 'text-red-800'
                                : 'text-amber-800'
                            }`}
                          >
                            {profitOrLoss > 0
                              ? `You made a profit of ${formatCurrency(profitOrLoss)} with this case.`
                              : profitOrLoss < 0
                              ? `You are making a loss of ${formatCurrency(Math.abs(profitOrLoss))} with this case.`
                              : 'This case is exactly at break-even.'}
                          </p>
                          <p className="mt-2 text-[12px] font-medium text-slate-500">
                            Profit / Loss updates live as quantity, pricing, and discount change.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <FinanceLockGate
              title="Finance Lock enabled. Enter PIN to continue."
              description="Payment actions create invoices, EMI schedules, and billing records."
            >
            <section className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-[0_18px_42px_-36px_rgba(15,23,42,0.24)]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-500">
                    Payment
                  </p>
                  <h2 className="mt-1 text-[22px] font-semibold tracking-[-0.01em] text-slate-900">
                    Choose Payment Method
                  </h2>
                </div>

                {invoiceLocked ? (
                  <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-[12px] font-semibold text-emerald-700">
                    Billing locked after invoice creation
                  </div>
                ) : null}
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <button
                    type="button"
                    disabled={savingInvoice || processingPayment || invoiceLocked}
                    onClick={() => {
                      setPaymentMode('one-time');
                      setEmiStep(1);
                      handleOneTimePayment();
                    }}
                    className="group flex min-h-[132px] w-full items-center justify-between gap-5 rounded-[26px] border border-blue-200 bg-[linear-gradient(135deg,_#2563eb_0%,_#4f46e5_100%)] px-6 py-5 text-left text-white shadow-[0_24px_48px_-30px_rgba(37,99,235,0.75)] transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-30px_rgba(37,99,235,0.9)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_24px_48px_-30px_rgba(37,99,235,0.75)] max-[480px]:flex-col max-[480px]:items-start"
                  >
                    <div className="min-w-0">
                      <p className="text-[18px] font-semibold tracking-[-0.01em]">
                        One-Time Payment
                      </p>
                      <p className="mt-2 text-[13px] font-medium text-blue-100">
                        Collect the full amount now.
                      </p>
                    </div>
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border border-white/30 bg-white/18 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] transition group-hover:bg-white/24">
                      {savingInvoice || processingPayment ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <Wallet className="h-7 w-7" />
                      )}
                    </span>
                  </button>

                  {showOneTimePaymentCard ? renderPaymentReadyCard() : null}
                </div>

                <div>
                  <button
                    type="button"
                    disabled={invoiceLocked}
                    onClick={() => {
                      setPaymentMode('emi');
                      setEmiStep(1);
                    }}
                    className={`group flex min-h-[132px] w-full items-center justify-between gap-5 rounded-[26px] border px-6 py-5 text-left shadow-[0_20px_44px_-34px_rgba(79,70,229,0.55)] transition-all hover:-translate-y-0.5 hover:border-violet-300 hover:bg-violet-50 hover:shadow-[0_26px_56px_-34px_rgba(79,70,229,0.75)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 max-[480px]:flex-col max-[480px]:items-start ${
                      paymentMode === 'emi'
                        ? 'border-violet-300 bg-violet-50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-[18px] font-semibold tracking-[-0.01em] text-slate-900">
                        EMI Payment
                      </p>
                      <p className="mt-2 text-[13px] font-medium text-slate-500">
                        Split the balance into monthly instalments.
                      </p>
                    </div>
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border border-violet-200 bg-violet-100 text-violet-700 transition group-hover:bg-violet-200">
                      <CreditCard className="h-7 w-7" />
                    </span>
                  </button>

              {paymentMode === 'emi' && (!invoiceLocked || showEmiPaymentCard) ? (
                <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50/80 p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                          emiStep === step
                            ? 'bg-indigo-600 text-white'
                            : 'border border-slate-200 bg-white text-slate-500'
                        }`}
                      >
                        Step {step}
                      </div>
                    ))}
                  </div>

                  {emiStep === 1 ? (
                    <div className="mt-5 space-y-3">
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          How much down payment are you taking now?
                        </label>
                        <input
                          type="number"
                          value={downPayment}
                          onChange={(event) => setDownPayment(event.target.value)}
                          className="mt-2 w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3.5 text-[14px] font-semibold text-slate-700 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                        />
                      </div>

                      <p className="text-[12px] font-medium text-slate-500">
                        Net payable for this case: {formatCurrency(netPayable)}
                      </p>
                    </div>
                  ) : null}

                  {emiStep === 2 ? (
                    <div className="mt-5 space-y-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Choose EMI duration
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['3', '6', '12', 'custom'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setEmiPlan(option)}
                            className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                              emiPlan === option
                                ? 'bg-indigo-600 text-white'
                                : 'border border-slate-200 bg-white text-slate-600'
                            }`}
                          >
                            {option === 'custom' ? 'Custom' : `${option} Months`}
                          </button>
                        ))}
                      </div>

                      {emiPlan === 'custom' ? (
                        <input
                          type="number"
                          min="1"
                          value={customEmiMonths}
                          onChange={(event) => setCustomEmiMonths(event.target.value)}
                          placeholder="Enter custom EMI months"
                          className="w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3.5 text-[14px] font-semibold text-slate-700 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {emiStep === 3 ? (
                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          First EMI Date
                        </label>
                        <input
                          type="date"
                          value={emiStartDate}
                          onChange={(event) => setEmiStartDate(event.target.value)}
                          className="mt-2 w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3.5 text-[14px] font-semibold text-slate-700 outline-none transition-all focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                        />
                      </div>

                      <div className="rounded-[18px] border border-indigo-100 bg-indigo-50 px-4 py-4">
                        <p className="text-[12px] font-semibold text-indigo-900">
                          EMI Summary
                        </p>
                        <div className="mt-3 space-y-2 text-[13px] font-medium text-indigo-800">
                          <p>Down Payment: {formatCurrency(downPaymentAmount)}</p>
                          <p>Remaining Balance: {formatCurrency(remainingAfterDownPayment)}</p>
                          <p>EMI Duration: {emiMonths || 0} month(s)</p>
                          <p>Approx Monthly EMI: {formatCurrency(monthlyEmiAmount)}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-5 flex flex-wrap justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleEmiBack}
                      disabled={emiStep === 1 || savingInvoice || processingPayment}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Back
                    </button>

                    {emiStep < 3 ? (
                      <button
                        type="button"
                        onClick={handleEmiNext}
                        className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_24px_-20px_rgba(15,23,42,0.45)] transition-colors hover:bg-indigo-700"
                      >
                        Next
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleEmiPayment}
                        disabled={savingInvoice || processingPayment || invoiceLocked}
                        className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_24px_-20px_rgba(15,23,42,0.45)] transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {savingInvoice || processingPayment ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4" />
                        )}
                        Finalize EMI Billing
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
                  {showEmiPaymentCard ? renderPaymentReadyCard() : null}
                </div>
              </div>
            </section>
            </FinanceLockGate>
          </div>
        </div>
      </div>
    </div>
  );
}
